import type { Moment } from 'moment'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useTripCollaborators } from 'tripPlanner/hooks/api/collaborator'
import UserAvatar from 'tripPlanner/Tripkit/Share/UserAvatar'
import { formatCasualDateTime } from 'tripPlanner/utils'

const AvatarContainer = styled.div`
  /* Set width using px not rems so it will match size with icons in context menus */
  width: 28px;
  height: 28px;
  margin-inline: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface Props {
  tripId: string
  createdAt: Moment
  updatedAt: Moment
  createdBy: string
  updatedBy: string
  avatarSize: 'small' | 'medium'
}

export default function AddedUpdatedBy({
  createdAt,
  createdBy,
  tripId,
  updatedAt,
  updatedBy,
  avatarSize,
}: Props) {
  const intl = useIntl()
  const isUpdated = !updatedAt.isSame(createdAt)

  const userId = isUpdated ? updatedBy : createdBy
  const time = isUpdated ? updatedAt : createdAt

  const { data: collaborators } = useTripCollaborators({ tripId })

  const owner = collaborators?.collaborators?.find((user) => user.id === userId)

  const lastUpdatedByLabel = owner ? intl.formatMessage(
    {
      id: '07yJQ5xCFFU9gnyla1NC',
      defaultMessage: 'Last updated by {name}',
      description: 'Shows who last updated this trip item in the trip planner feature',
    },
    { name: owner.givenName },
  ) : ''
  const createdByLabel = owner ? intl.formatMessage(
    {
      id: 'RLnEYSB3U1KV_MMvh0ft',
      defaultMessage: 'Created by {name}',
      description: 'Shows who created this trip item in the trip planner feature',
    },
    { name: owner.givenName },
  ) : ''

  return (
    <Group
      direction="horizontal"
      verticalAlign="center"
      horizontalAlign="start"
      gap={12}
    >
      {owner && (
        <>
          <AvatarContainer>
            <UserAvatar
              firstName={owner.givenName}
              lastName={owner.surname}
              variant="primary"
              size={avatarSize}
            />
          </AvatarContainer>
          <div>
            <BodyText variant="medium">
              {isUpdated ? lastUpdatedByLabel : createdByLabel}
            </BodyText>
            <BodyText variant="small" colour="light-foreground-alt">
              {formatCasualDateTime(time)}
            </BodyText>
          </div>
        </>
      )}
    </Group>
  )
}
